import { PreCheckStatus } from "@/components/pages/settlement/types/settlementBooking";
import { DocumentType } from "@/types/document";
import { ReservationStatus } from "@/types/reservation";
import { z } from "zod";
import { GuestType } from "@/types/guest";

const today = new Date();
today.setHours(0, 0, 0, 0);

const uploadDocumentSchema = z.object({
  id: z.string().optional(),
  fileId: z.string().optional(),
  type: z.enum(DocumentType),
  file: z.instanceof(File).optional(),
  image: z.string().optional(),
});

const nameRegex = /^[a-zA-Zа-яА-ЯёЁіІїЇєЄґҐ\s'-]+$/;

export type ReservationSchemaMessages = {
  firstNameRequired: string;
  lastNameRequired: string;
  nameFormat: string;
  emailRequired: string;
  emailInvalid: string;
  phoneRequired: string;
  phoneDigits: string;
  countryRequired: string;
  dateOfBirthFuture: string;
  roomRequired: string;
  arrivalDateRequired: string;
  arrivalDatePast: string;
  departureDateRequired: string;
  departureDatePast: string;
  departureAfterArrival: string;
  estimatedArrivalRequired: string;
  estimatedArrivalPast: string;
};

export function buildReservationSchemas(messages: ReservationSchemaMessages) {
  const additionalGuestSchema = z.object({
    guestType: z.enum(GuestType),
    firstName: z
      .string()
      .trim()
      .min(1, messages.firstNameRequired)
      .regex(nameRegex, messages.nameFormat),
    lastName: z
      .string()
      .trim()
      .min(1, messages.lastNameRequired)
      .regex(nameRegex, messages.nameFormat),
    email: z
      .string()
      .min(1, messages.emailRequired)
      .email(messages.emailInvalid),
    phone: z.string().optional(),
    language: z.string().optional(),
    dateOfBirth: z.string().refine(
      (val) => {
        if (!val) return true;
        const date = new Date(val);
        return date <= today;
      },
      {
        message: messages.dateOfBirthFuture,
      },
    ),
  });

  const reservationSchema = z
    .object({
      photoId: z.string().nullish(),

      firstName: z
        .string()
        .trim()
        .min(1, messages.firstNameRequired)
        .regex(nameRegex, messages.nameFormat),

      lastName: z
        .string()
        .trim()
        .min(1, messages.lastNameRequired)
        .regex(nameRegex, messages.nameFormat),

      email: z
        .string()
        .min(1, messages.emailRequired)
        .email(messages.emailInvalid),

      phone: z
        .string()
        .min(1, messages.phoneRequired)
        .refine(
          (val) => {
            const clean = val.replace(/\D/g, "");
            return clean.length >= 10 && clean.length <= 15;
          },
          {
            message: messages.phoneDigits,
          },
        ),

      language: z.string().trim().min(1, messages.countryRequired),

      dateOfBirth: z.string().refine(
        (val) => {
          if (!val) return true;
          const date = new Date(val);
          return date <= today;
        },
        {
          message: messages.dateOfBirthFuture,
        },
      ),

      roomId: z.string().trim().min(1, messages.roomRequired),

      arrivalDate: z
        .string()
        .min(1, messages.arrivalDateRequired)
        .refine(
          (val) => {
            const date = new Date(val);
            return date >= today;
          },
          {
            message: messages.arrivalDatePast,
          },
        ),

      departureDate: z
        .string()
        .min(1, messages.departureDateRequired)
        .refine(
          (val) => {
            const date = new Date(val);
            return date >= today;
          },
          {
            message: messages.departureDatePast,
          },
        ),
      comments: z.string().optional(),

      paymentStatus: z.string().optional(),

      additionalGuests: z.array(additionalGuestSchema).optional(),

      documents: z.array(uploadDocumentSchema).optional(),
    })
    .refine(
      (data) => {
        const arrival = new Date(data.arrivalDate);
        const departure = new Date(data.departureDate);
        return departure > arrival;
      },
      {
        message: messages.departureAfterArrival,
        path: ["departureDate"],
      },
    );

  const updateReservationSchema = z
    .object({
      guestId: z.string().optional(),

      photoId: z.string().nullish(),

      firstName: z
        .string()
        .trim()
        .min(1, messages.firstNameRequired)
        .regex(nameRegex, messages.nameFormat),

      lastName: z
        .string()
        .trim()
        .min(1, messages.lastNameRequired)
        .regex(nameRegex, messages.nameFormat),

      email: z
        .string()
        .min(1, messages.emailRequired)
        .email(messages.emailInvalid),

      phone: z
        .string()
        .min(1, messages.phoneRequired)
        .refine(
          (val) => {
            const clean = val.replace(/\D/g, "");
            return clean.length >= 10 && clean.length <= 15;
          },
          {
            message: messages.phoneDigits,
          },
        ),

      language: z.string().trim().min(1, messages.countryRequired),

      roomId: z.string().trim().min(1, messages.roomRequired),

      arrivalDate: z
        .string()
        .min(1, messages.arrivalDateRequired)
        .refine(
          (val) => {
            const date = new Date(val);
            return date >= today;
          },
          {
            message: messages.arrivalDatePast,
          },
        ),

      departureDate: z
        .string()
        .min(1, messages.departureDateRequired)
        .refine(
          (val) => {
            const date = new Date(val);
            return date >= today;
          },
          {
            message: messages.departureDatePast,
          },
        ),
      comments: z.string().optional(),

      additionalGuests: z.array(additionalGuestSchema).optional(),

      bookingNumber: z.number().optional(),

      bookingSource: z.string().optional(),

      status: z.enum(ReservationStatus).optional(),

      roomType: z.string().optional(),

      preCheckinStatus: z.enum(PreCheckStatus).optional(),

      preCheckinEnteredGuests: z.number().or(z.string()).optional(),

      checkinMethod: z.string().optional(),

      paymentStatus: z.string().optional(),

      estimatedTimeOfArrival: z
        .string()
        .min(1, messages.estimatedArrivalRequired)
        .refine(
          (val) => {
            const date = new Date(val);
            return date >= today;
          },
          {
            message: messages.estimatedArrivalPast,
          },
        ),

      documents: z.array(uploadDocumentSchema).optional(),

      notes: z.array(z.string()).optional(),
    })
    .refine(
      (data) => {
        const arrival = new Date(data.arrivalDate);
        const departure = new Date(data.departureDate);
        return departure > arrival;
      },
      {
        message: messages.departureAfterArrival,
        path: ["departureDate"],
      },
    );

  return {
    reservationSchema,
    updateReservationSchema,
    additionalGuestSchema,
  };
}

type BuiltSchemas = ReturnType<typeof buildReservationSchemas>;

export type ReservationUpdateFormData = z.infer<
  BuiltSchemas["updateReservationSchema"]
>;
export type ReservationFormData = z.infer<BuiltSchemas["reservationSchema"]>;
export type DocumentFormData = z.infer<typeof uploadDocumentSchema>;
